<template>
  <div>
    <MaintenanceNote />

    <div ref="nav" class="nav">
      <div v-if="hideBackButton" class="nav__left">
        <span class="title"> {{ title }}</span>
      </div>
      <div v-else class="nav__left">
        <img v-if="!hideBackButton" :src="require('@/assets/img/arrow_back.png')" @click="back" />
        <span class="title"> {{ title }} </span>
      </div>
      <div class="nav__right">
        <div
          v-if="showLanguageSelector"
          class="locale-btn"
          :class="{ 'mr-1': showMyRequest || actionType.type === ActionEnum.Login, 'mr-3': hideAction }"
          @click.stop="showChangeLanguage"
        >
          <LanguageIcon class="locale-img" />
          <span class="local-btn-label">{{ localeName }}</span>
        </div>
        <template v-if="!hideAction">
          <div
            v-if="showMyRequest"
            v-track-click="BTN_EVTS.HOME_HEADER_MY_REQUESTS"
            class="my-requests"
            @click="clickAction"
          >
            {{ $t('global.my_requests') }}
          </div>
          <!-- Dont show "Register/Log In" when Not Logged In Web  -->
          <div v-else-if="actionType.type === ActionEnum.NotLoginWeb" />
          <div
            v-else-if="!hideMyRequests && actionType.type === ActionEnum.MyRequest"
            class="login"
            :class="{ 'mr-0-imp': platform === 'wap' }"
            @click="clickAction"
          >
            {{ actionType.msg }}
          </div>
          <div v-if="isChatScreen" ref="dot-icon" class="dot-icon" @click.stop="handleDot">
            <div v-for="dot in 3" :key="dot" class="dot" />
          </div>
        </template>
      </div>
      <div
        v-if="showDropdown"
        ref="dropdown"
        v-clickOutside.showDropdown="{
          exclude: ['dropdown', 'dot-icon'],
          handler: 'closeDropdown',
        }"
        class="droplist"
        :style="{ top: `${navHeight}px` }"
      >
        <div
          v-if="!directLink"
          v-track-click="BTN_EVTS.HOME_HEADER_MENU_MINIMIZE"
          class="droplist__item"
          :class="{ hide: !enableMinimize }"
          @click.stop="handleMinimize"
        >
          {{ $t('header.minimize') }}
        </div>
        <div v-if="showManageConsentButton" class="droplist__item" @click.stop="handleManageConsent">Data Consent</div>
        <div
          v-if="enableEndChat"
          v-track-click="`${trackEventContext}${COMPONENT_EVENTS.HEADER_END_CHAT}`"
          class="droplist__item"
          @click.stop="handleEndChat"
        >
          {{ $t('global.end_chat') }}
        </div>
      </div>

      <ManageConsentGA :show="showManageConsentModel" @close="closeManageConsent" />
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { platform, Bridge } from '@/common/bridge';
import { namespace } from 'vuex-class';
import { emitOpenMyRequest } from '@/common/eventBus';
import clickOutside from '@/directives/clickOutside';
import LanguageSelector from '@/components/LanguageSelector.vue';
import ManageConsentGA from '@/components/ManageConsentGA.vue';
import { LANGUAGES, SupportedLanguages } from '@/plugins/i18n';
import * as env from '@/common/environment';
import LanguageIcon from './icons/LangaugeIcon.vue';
import trackClick, { BTN_EVTS } from '@/directives/trackClick';
import { analytics, syncLogs, COMPONENT_EVENTS } from '@/common/logs';
import MaintenanceNote from './MaintenanceTools/MaintenanceNote.vue';

const userModule = namespace('user');
const chatModule = namespace('chat');
const analyticsConsentModule = namespace('analyticsConsent');

enum ActionEnum {
  MyRequest,
  Login,
  NotLoginWeb,
}

@Component({
  components: { LanguageIcon, ManageConsentGA, MaintenanceNote, LanguageSelector },
  directives: {
    clickOutside,
    trackClick,
  },
})
export default class Header extends Vue {
  declare $refs: Vue['$refs'] & {
    nav: HTMLDivElement;
    dropdown?: HTMLDivElement;
    'dot-icon'?: HTMLDivElement;
  };
  @Prop({ default: '' }) readonly title!: string;
  @Prop({ default: () => {} }) readonly backCallback!: Function;
  @Prop({ default: false }) readonly isInlive!: boolean; // only show when LIveChat
  @Prop({ default: false }) readonly didVoiceChatConnect!: boolean;
  @Prop(Boolean) readonly hideAction!: boolean;
  @Prop({ default: () => {} }) readonly endChatCallback!: Function;
  @Prop(Boolean) readonly isChatScreen!: boolean;
  @Prop(Boolean) readonly hideLanguageSelector!: boolean;
  @Prop(Boolean) readonly isActiveConversationSelected!: boolean;
  @Prop(Boolean) readonly hideMyRequests!: boolean;
  @Prop(Boolean) readonly forceHideBack!: boolean;
  @Prop(Number) readonly ticketId!: number;
  @Prop(String) readonly trackEventContext!: string;

  @userModule.State('isLogin') isLogin!: boolean;
  @userModule.State('directLink') directLink!: boolean;
  @chatModule.Mutation('updateNewMessage') updateNewMessage!: Function;

  @userModule.State('hideLoginButton') hideLoginButton!: boolean;
  @analyticsConsentModule.Getter('showManageConsentButton') showManageConsentButton!: boolean;

  readonly BTN_EVTS = BTN_EVTS;
  readonly COMPONENT_EVENTS = COMPONENT_EVENTS;

  showDropdown = false;
  showManageConsentModel = false;
  remainOpen = true;
  platform = platform;
  ActionEnum = ActionEnum;
  navHeight = 0;

  get showLanguageSelector() {
    return !this.hideLanguageSelector && env.get('showLanguageSelector');
  }

  get localeName() {
    const locale = this.$i18n.locale as SupportedLanguages;

    if (platform === 'wap') {
      return locale.toUpperCase();
    }

    return LANGUAGES[locale]?.name || locale.toUpperCase();
  }

  get showMyRequest() {
    return !this.hideMyRequests && this.isChatScreen && this.isLogin;
  }

  get hideBackButton() {
    const { forceHideBack, isActiveConversationSelected, isChatScreen, platform } = this;
    if (forceHideBack) return true;
    if (isActiveConversationSelected) return false;

    return isChatScreen && platform !== 'wap';
  }

  get actionType() {
    if (this.isLogin) {
      return {
        msg: this.$t('global.my_requests'),
        type: ActionEnum.MyRequest,
      };
    }
    if (this.platform === 'web' || this.hideLoginButton) {
      // not login web
      return {
        msg: '',
        type: ActionEnum.NotLoginWeb,
      };
    }
    return {
      msg: this.t('header.register'),
      type: ActionEnum.Login,
    };
  }

  get enableMinimize() {
    return this.isInlive || this.didVoiceChatConnect || this.platform === 'web' || this.isNative;
  }
  get enableEndChat() {
    if (this.platform === 'web') {
      return this.isInlive;
    }
    return true;
  }
  get isNative() {
    return this.platform === 'app' || this.platform === 'ios';
  }

  handleDot() {
    if (!this.showDropdown) {
      this.navHeight = this.$refs.nav.getBoundingClientRect().height;
      this.showDropdown = true;
      return;
    }
    this.showDropdown = false;
  }

  showChangeLanguage() {
    const dialog = this.$dialog({
      componentBackground: 'transparent',
      content: this.$createElement(LanguageSelector, {
        on: { closeDialog: () => dialog?.close() },
      }),
    });
  }

  private closeDropdown() {
    this.showDropdown = false;
  }

  private back() {
    this.showDropdown = false;

    analytics.trackClick({
      name: `${this.trackEventContext}${COMPONENT_EVENTS.HEADER_BACK}`,
      ...(this.ticketId ? { selection: this.ticketId } : {}),
    });

    if (this.backCallback) {
      this.backCallback();
      return;
    }
    this.$router.back();
  }

  async handleEndChat() {
    if (!this.enableEndChat) return;
    this.showDropdown = false;

    if (this.endChatCallback) {
      this.endChatCallback();
      return;
    }

    await syncLogs();

    if (this.platform) {
      Bridge.sendToParent('CLOSE');
    } else {
      this.back();
    }
  }

  handleManageConsent() {
    this.showDropdown = false;
    this.showManageConsentModel = true;
  }

  closeManageConsent() {
    this.showManageConsentModel = false;
  }

  private clickAction() {
    if (this.actionType.type === ActionEnum.MyRequest) {
      emitOpenMyRequest(true);
    } else if (this.actionType.type === ActionEnum.Login) {
      Bridge.sendToParent('LOGIN_POP');
    }
  }

  private handleMinimize() {
    this.showDropdown = false;
    if (!this.enableMinimize) return;
    Bridge.minimize();
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/base.scss';

.nav {
  min-height: $header-height;
  background-color: $header-background;
  color: $header-text-color;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;

  &__left {
    margin-left: 1.6rem;
    display: flex;
    align-items: center;
    .minimize {
      border-bottom: solid 2px #fff;
      width: 10px;
      cursor: pointer;
    }
    img {
      width: 1.2rem;
      cursor: pointer;
    }
    .title {
      margin-left: 1.6rem;
      font-size: $size-m;
      &.center {
        margin-left: 0;
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
      }
    }
  }
  &__right {
    display: flex;
    align-items: stretch;
    position: relative;

    .my-requests,
    .locale-btn {
      background-color: $header-buttons-background;
      padding: 0.6rem 1.1rem;
      border-radius: 0.2rem;
      color: $header-buttons-text-color;
      cursor: pointer;
      font-size: 1.2rem;
    }

    .locale-btn {
      padding-top: 3px;
      padding-bottom: 3px;
      display: flex;
      align-items: center;

      .locale-img {
        width: 18px;
        height: auto;
        margin-right: 4px;
      }
    }

    .login {
      font-size: $size-m2;
      border: solid 0.1rem $white;
      border-radius: 0.2rem;
      padding: 0.5rem 1.4rem;
      margin-right: 1.6rem;
      cursor: pointer;
    }
    .dot-icon {
      display: flex;
      flex-direction: column;
      position: relative;
      right: 0;
      height: 2.5rem;
      padding: 0.1rem 2rem;
      margin-left: 1.6rem;
      justify-content: space-between;
      align-items: center;
      cursor: pointer;
      > * {
        cursor: auto;
      }
      .dot {
        height: 5px;
        width: 5px;
        background: $white;
        border-radius: 50%;
      }
    }
  }
  .droplist {
    position: absolute;
    background: $header-menu-background;
    display: flex;
    top: $header-height;
    right: 0;
    z-index: 10000;
    border-radius: 2px;
    box-shadow: -1px 1px 10px 1px rgba(0, 0, 0, 0.15);
    border-style: solid;
    border-width: 0.5px;
    flex-direction: column;
    min-width: 100px;
    &__item {
      padding: 1.4rem 1.8rem;
      font-size: 1.4rem;
      color: $header-menu-item-text-color;
      cursor: pointer;
      &.disabled {
        color: $header-menu-item-disabled;
        cursor: auto;
      }
      &.hide {
        display: none;
      }
    }
  }
}
</style>
